import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, articleBySlug } from '@/mocks/articles';
import { columnMap, site } from '@/mocks/columns';

function useReadingProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setP(total > 0 ? Math.min(1, el.scrollTop / total) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return p;
}

export default function ArticleDetail() {
  const { slug = '' } = useParams();
  const article = articleBySlug[slug];
  const progress = useReadingProgress();

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title="文章未找到 - 看料社"
          description="该文章不存在或已被调整。返回看料社浏览更多深扒与专栏分析内容。"
          canonical={site.domain}
        />
        <Header />
        <main className="mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="font-heading text-6xl font-black text-background-200">404</span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这篇文章</h1>
          <p className="mt-2 text-sm text-foreground-600">文章可能已被移除，去看看其他深扒吧。</p>
          <Link
            to="/lanmu"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700"
          >
            去栏目总览
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const col = columnMap[article.column];
  const related = articles
    .filter((a) => a.slug !== article.slug)
    .sort((a, b) => (a.column === article.column ? -1 : 1) - (b.column === article.column ? -1 : 1))
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.lead,
    image: article.cover,
    datePublished: article.date,
    author: { '@type': 'Person', name: `看料社编辑部 · ${article.author}` },
    publisher: { '@type': 'Organization', name: '看料社', url: site.domain },
    inLanguage: 'zh-CN',
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${site.domain}/wenzhang/${article.slug}` },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${article.title} - 看料社${col?.name ?? '专栏'}·小编深扒`}
        description={article.lead}
        keywords={`看料社,${article.title},${col?.name},小编深扒,专栏分析,深度扒文`}
        canonical={`${site.domain}/wenzhang/${article.slug}`}
        jsonLd={jsonLd}
      />
      <Header />

      <div className="fixed left-0 top-16 z-40 h-0.5 bg-primary-500" style={{ width: `${progress * 100}%` }} />

      <main>
        <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1">
              <li>
                <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              </li>
              <li aria-hidden="true" className="text-foreground-400">/</li>
              <li>
                <Link to="/lanmu" className="transition-colors hover:text-primary-600">看料栏目</Link>
              </li>
              <li aria-hidden="true" className="text-foreground-400">/</li>
              <li>
                <Link to={`/lanmu/${article.column}`} className="transition-colors hover:text-primary-600">
                  {col?.name}
                </Link>
              </li>
              <li aria-hidden="true" className="text-foreground-400">/</li>
              <li className="truncate text-foreground-800" aria-current="page">正文</li>
            </ol>
          </nav>

          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Link
                to={`/lanmu/${article.column}`}
                className="rounded-sm bg-primary-100 px-2.5 py-1 font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                {col?.name}
              </Link>
              <span className="text-foreground-500">{article.date}</span>
              <span className="text-foreground-400">·</span>
              <span className="text-foreground-500">{article.readTime}阅读</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-5 flex items-center gap-3 border-y border-background-200 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-800">
                {article.author.slice(0, 1)}
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">看料社编辑部 · {article.author}</p>
                <p className="text-xs text-foreground-500">主编审核 · 信源经编辑部核实</p>
              </div>
            </div>
          </div>

          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看料社小编深扒`}
            className="mt-7 w-full rounded-lg object-cover object-top"
          />

          <div className="prose-prompt mt-8">
            <p className="border-l-2 border-accent-500 bg-accent-50/70 px-5 py-4 font-heading text-base font-medium leading-relaxed text-foreground-800 md:text-lg">
              {article.lead}
            </p>
            <div className="mt-6 space-y-6 text-[15px] leading-[1.9] text-foreground-800 md:text-base">
              {article.content.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
              >
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-600 text-base font-bold text-background-50">
              看
            </span>
            <div>
              <p className="font-heading text-base font-bold text-foreground-950">看料社 · 编辑部立场说明</p>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                本文为编辑部整理分析的深扒内容，仅代表编辑部视角。能核实的信息已尽量标注依据，推测部分均明确说明，供读者参考与继续求证，不构成对任何方的定论。
              </p>
            </div>
          </div>
        </article>

        {/* 相关推荐 */}
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              更多值得深扒的内容
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}